import { createMemo, createRoot } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import { GameNode } from "@/types/swagger/front";
import { ComponentInfo, GetConfigVersionRsp } from "@/types/swagger/front.ts";

import type { FrontCfgV2Map } from "../types/config.ts";
import { setGameStore } from "./gameStore";

export const [noticeConfigStore, setNoticeConfigStore] = createStore(
  {} as {
    status: boolean;
    content: [];
    [key: string]: unknown;
  },
);

const [store, setConfigStore] = createStore({
  frontCfgMap: {} as FrontCfgV2Map,
  componentListMap: {} as Record<string, ComponentInfo[]>,
  // 原始组件列表
  originalCompListMap: {} as Record<string, ComponentInfo[]>,
  configVersion: {} as GetConfigVersionRsp, // 配置版本
  renderedMap: {} as Record<string, boolean>,
  version: "",
  android_app_link: "",
  app_download_links: [] as string[], // 安卓app下载链接
  android_app_download_link_config: [] as string[], // 安卓app下载链接配置
});

export const configStore = store;

export const setFrontCfgMap = (frontCfgMap: FrontCfgV2Map) => {
  const { NoticeConfig, ...filtered } = frontCfgMap;

  setNoticeConfigStore({ ...(NoticeConfig || {}) });
  setConfigStore("frontCfgMap", reconcile(filtered));
};

export const setComponentListMap = (
  componentListMap: Record<string, ComponentInfo[]>,
) => {
  setConfigStore("componentListMap", reconcile(componentListMap));
};

// 设置原始组件
export const setOriginalCompListMap = (
  originalCompListMap: Record<string, ComponentInfo[]>,
) => {
  setConfigStore("originalCompListMap", reconcile(originalCompListMap));
};

// 更新组件列表
export const updateComponentListMap = (game: GameNode) => {
  const targetKeys = ["home", "casino"];

  const comp = JSON.parse(JSON.stringify(configStore.originalCompListMap));

  const targetComponents = targetKeys.flatMap(
    (key) =>
      comp[key]?.filter(
        (item) => item.type === "game_list" && item.style === 3,
      ) || [],
  );
  targetComponents.forEach((item) => {
    if (item.type === "game_list" && item.style === 3) {
      item.games = [game];
      setGameStore("continueGameList", [game]);
    }
  });
  setComponentListMap(comp);
};

export const setConfigVersion = (configVersion: GetConfigVersionRsp) => {
  console.log("setConfigVersion configVersion ", configVersion);
  setConfigStore("configVersion", reconcile(configVersion));
};

export const setVersion = (version: string) => {
  setConfigStore("version", version);
};

// 设置安卓app下载链接
export const setAndroidAppDownloadLinks = (app_links: string[]) => {
  setConfigStore("app_download_links", app_links);
};

// 设置安卓app下载链接配置
export const setAndroidAppDownloadLinkConfig = (links: string[]) => {
  setConfigStore("android_app_download_link_config", links);
};

// 响应式计算：获取安卓app下载链接
const android_app_download_link = () => {
  if (!configStore.android_app_link) {
    if (configStore.app_download_links?.length > 0) {
      setConfigStore("android_app_link", configStore.app_download_links?.[0]);
    } else if (configStore.android_app_download_link_config?.length > 0) {
      setConfigStore(
        "android_app_link",
        configStore.android_app_download_link_config?.[0],
      );
    }
  }
  return configStore.android_app_link;
};

// 获取安卓app下载链接（函数式API，保持向后兼容）
export const getAndroidAppDownloadLink = () => {
  return android_app_download_link();
};

// 获取活动安卓google play app下载链接
export const getAppDownloadActGooglePlayUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appDownloadActCfg
        .googleplayUrl || ""
    );
  }),
);

// 获取活动iOSapp下载链接
export const getAppStoreUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appDownloadActCfg
        .appStoreUrl || ""
    );
  }),
);

// 获取活动安卓app下载链接
export const getAppDownloadActAndroidUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appDownloadActCfg
        .apkDownloadUrl || ""
    );
  }),
);

// 获取活动安卓默认app下载链接
export const getAppDownloadDefaultUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appInstallCfg
        .apkDefaultDownloadUrl || ""
    );
  }),
);

// 获取活动外部app下载链接
export const getExternalUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appDownloadActCfg
        .externalUrl || ""
    );
  }),
);

// 获取下载页安卓google play app下载链接
export const getInstallGooglePlayUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appInstallCfg?.googleplayUrl ||
      ""
    );
  }),
);

// 获取下载页安卓google play app下载链接
export const getInstallGooglePlayLiteUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appInstallCfg
        ?.googleplayliteUrl || ""
    );
  }),
);

export const getInstallPwaAppUrl = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["SeoCfg"]?.app_url || "";
  }),
);

// 获取下载页iOSapp下载链接
export const getInstallAppStoreUrl = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appInstallCfg?.appStoreUrl ||
      ""
    );
  }),
);

// 获取下载页PWA开关
export const installPwaSwitch = createRoot(() =>
  createMemo(() => {
    return (
      configStore.frontCfgMap["PlatInstallCfg"]?.appInstallCfg?.pwaSwitch ||
      false
    );
  }),
);
/**
 * 更新组件的渲染状态
 *
 * @param key 组件key
 * @param rendered 是否已渲染
 */
export const updateRenderedMap = (key: string, rendered: boolean) => {
  setConfigStore("renderedMap", key, rendered);
};

/**
 * 获取组件的渲染状态
 *
 * @param key 组件key
 * @returns 组件的渲染状态，如果未设置则返回 false
 */
export const getRenderedStatus = (key: string): boolean => {
  return configStore.renderedMap[key] ?? false;
};

const mergedComponents = createRoot(() =>
  createMemo(() => Object.values(configStore.componentListMap).flat()),
);

export const appName = createRoot(() =>
  createMemo(() => {
    const frontCfg = configStore.frontCfgMap;
    const appName = frontCfg["SeoCfg"]?.app_name || "";
    return appName;
  }),
);

export const getSeoConfig = createRoot(() =>
  createMemo(() => {
    const frontCfg = configStore.frontCfgMap;
    return frontCfg["SeoCfg"] || {};
  }),
);

// 分享媒体配置
export const getShareListCfg = createRoot(() =>
  createMemo(() => {
    const hasMoreIcon = configStore.frontCfgMap[
      "ShareListCfg"
    ]?.share_list?.some((item) => item.icon === "more");
    if (!hasMoreIcon) {
      configStore.frontCfgMap["ShareListCfg"]?.share_list?.push({
        icon: "more",
        name: "More",
      });
    }
    return configStore.frontCfgMap["ShareListCfg"] &&
      configStore.frontCfgMap["ShareListCfg"]?.hide === false
      ? configStore.frontCfgMap["ShareListCfg"]?.share_list.filter(
          (item) => item?.status,
        )
      : [];
  }),
);

// 社交媒体配置
export const getSocialMediaCfg = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["SocialMediaCfg"] &&
      configStore.frontCfgMap["SocialMediaCfg"]?.share_hide === false
      ? configStore.frontCfgMap["SocialMediaCfg"]?.list.filter(
          (item) => item?.h5_hide,
        )
      : [];
  }),
);

export const getAppConfig = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["AppConfig"] || {};
  }),
);

// 首页浮标
export const getBuoyListCfg = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["NewBuoyList"] || {};
  }),
);

// 全局基础配置
export const getWebsiteConfigV2 = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["WebsiteConfigV2"] || {};
  }),
);

//游戏组件默认配置
export const getGameItemConfig = createRoot(() =>
  createMemo(() => {
    return configStore.frontCfgMap["GameConfig"] || {};
  }),
);

export const getComponentInfoByKey = (key: string) => {
  return (
    mergedComponents().find((item: ComponentInfo) => item.key === key) || null
  );
};

export const FooterComponents = [
  "policy",
  "media",
  "pay",
  "foot_info",
  "store_download",
];
